<script setup lang="ts">
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { ChevronRight } from "lucide-vue-next";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";

const props = withDefaults(
  defineProps<{
    id: string;
    title: string;
    count?: number;
  }>(),
  { count: 0 },
);

const { openOptionSection } = storeToRefs(useResultsV2CardsStore());
const open = computed(() => openOptionSection.value === props.id);

function toggle() {
  openOptionSection.value = open.value ? "" : props.id;
}
</script>

<template>
  <Collapsible
    :open="open"
    as="section"
    :data-option-section="id"
    @update:open="toggle">
    <CollapsibleTrigger
      class="-mx-1 flex w-[calc(100%+0.5rem)] items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-muted">
      <ChevronRight
        class="size-3.5 shrink-0 text-muted-foreground transition-transform duration-200"
        :class="{ 'rotate-90': open }" />
      <span class="heading-label font-bold text-foreground">{{ title }}</span>
      <span
        v-if="count"
        class="inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-3xs font-semibold tabular-nums"
        :class="open ? 'bg-primary/10 text-primary' : 'bg-primary text-primary-foreground'"
        :title="`${count} option${count > 1 ? 's' : ''} applied`">
        {{ count }}
      </span>
      <span class="h-px flex-1 bg-border" />
    </CollapsibleTrigger>
    <CollapsibleContent>
      <div class="space-y-3 pt-3 pb-1">
        <slot />
      </div>
    </CollapsibleContent>
  </Collapsible>
</template>
